import { memo, useCallback, useEffect, useState } from 'react';
import Layout from '../../../../components/layout';
import Breadcrumb from '../../../../components/common/breadcrumb';

import { Form, Input, Button, Flex, Spin } from 'antd';
import { LinkOutlined } from '@ant-design/icons';

import { User } from '../../../../fsm/users';
import { checkSchedulingLink } from '../../utils/schedulingLink';
import Availability from '../availability';
import styles from './index.module.css';

interface Props {
  user?: User;
  isLoading: boolean;
  onUpdateUser: (data: Partial<User>, id: number) => void;
}

const Calendar = ({ user, isLoading, onUpdateUser }: Props) => {
  const [disabled, setDisabled] = useState(Boolean(user?.calendarLinkUrl));
  const [resolved, setResolved] = useState<string | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        link: user.calendarLinkUrl,
      });
    }
  }, [user, form]);

  useEffect(() => {
    if (user?.calendarLinkUrl) {
      setDisabled(true);
    }
  }, [user?.calendarLinkUrl]);

  const handleEdit = useCallback(() => {
    setDisabled(false);
  }, [setDisabled]);

  /**
   * Shows what the typed value resolves to, so someone who pasted
   * "calendly.com/dana" can see the https:// form that candidates receive
   * before they save. The stored value is still exactly what they typed --
   * rewriting it silently would be its own small dishonesty.
   */
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const check = checkSchedulingLink(e.target.value);
    setResolved(
      check.ok && check.resolved !== e.target.value.trim()
        ? (check.resolved ?? null)
        : null,
    );
  }, []);

  const handleSave = useCallback(async () => {
    try {
      await form.validateFields();
      const calendarLinkUrl = form.getFieldValue('link');

      if (user?.id) {
        onUpdateUser(
          {
            calendarLinkUrl,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            role: user.role,
          },
          user?.id,
        );
      }
    } catch (errorInfo) {
      // antd surfaces field-level errors from validateFields itself; this is
      // only here so an unexpected failure is not swallowed entirely.
      console.error('Validation failed:', errorInfo);
    }
  }, [form, user?.id, onUpdateUser]);

  return (
    <Layout user={user}>
      {/* Matches the sidebar nav label ("Calendar settings") -- every other
          page in this nav (Dashboard, Job list) uses the identical string in
          both places, and this page's own content is entirely a settings
          form, not a calendar view. */}
      <Breadcrumb
        renderLeft={<h1 className={styles.pageTitle}>Calendar settings</h1>}
      />
      {isLoading ? (
        <Flex align="center" justify="center" className={styles.loading}>
          <Spin tip="Loading..." />
        </Flex>
      ) : (
        <div className={styles.wrapper}>
          <div className={styles.content}>
            <div className={styles.linkCard}>
              <div className={styles.linkCardHeader}>
                <span className={styles.linkCardIcon} aria-hidden="true">
                  <LinkOutlined />
                </span>
                <div>
                  <h2 className={styles.linkCardTitle}>Calendar link</h2>
                  <p className={styles.intro}>
                    Insert the scheduling link from your calendar app so Rio
                    can schedule interviews with candidates.
                  </p>
                </div>
              </div>
              <Form
                form={form}
                layout="vertical"
                className={styles.form}
                autoComplete="off"
              >
                <Form.Item
                  name="link"
                  rules={[
                    {
                      /*
                       * This field used to carry `required: true` and nothing
                       * else, so any non-empty string saved. It is the link
                       * Rio hands to candidates, so "asdf" saved cleanly and
                       * the failure surfaced to a candidate holding a dead
                       * link -- the person least able to do anything about
                       * it.
                       *
                       * Not antd's `type: 'url'`, which demands a scheme and
                       * so rejects "calendly.com/dana" -- exactly what
                       * someone copying from their calendar app ends up with.
                       */
                      validator: async (_rule, value) => {
                        const check = checkSchedulingLink(value ?? '');
                        if (!check.ok) throw new Error(check.reason);
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder="yourcalendar.com/your-name"
                    size="large"
                    disabled={disabled}
                    onChange={handleChange}
                  />
                </Form.Item>
                {resolved && !disabled && (
                  <p className={styles.resolved}>
                    Candidates will be sent <strong>{resolved}</strong>
                  </p>
                )}
                {disabled ? (
                  <Button type="primary" onClick={handleEdit}>
                    Edit
                  </Button>
                ) : (
                  <Button type="primary" onClick={handleSave}>
                    Save
                  </Button>
                )}
              </Form>
            </div>
          </div>
          <div className={styles.content}>
            <Availability userId={user?.id} />
          </div>
        </div>
      )}
    </Layout>
  );
};

export default memo(Calendar);
